import { accountZone } from "../ui/instant.js";

/**
 * FeedContentInspection
 *
 * What the server currently holds about one calendar, rendered for a person:
 * the events in the cached snapshot, and the events that disappeared from it.
 *
 * This is a development aid, not a customer feature, and the screen says so.
 * The calendar content it shows already crosses the wire in a content_mode
 * webhook — but only the events that changed, only at the moment they changed,
 * and only inside a signed delivery. Reading it here is different: it is on
 * demand, through a session cookie, and it can be re-read at will for as long as
 * the snapshot lives. That is why the deployment must switch it on
 * (`PANEL_FEED_CONTENT_INSPECTION`) and why the retention is printed rather than
 * implied.
 *
 * Everything a calendar carries that is not the event — PRODID, METHOD,
 * CALSCALE, VTIMEZONE blocks, transport parameters, the provider's X- extensions
 * — is already gone by the time an entry reaches this component, and the
 * remaining fields arrive as finished phrases. Nothing here re-formats a
 * timestamp or re-parses iCalendar: the server decides how an event reads, so the
 * screen and the webhook payload cannot describe the same event differently.
 *
 * Props:
 *   content — the mapped `GET /feeds/{id}/content` response
 *   now — injectable clock for the relative labels
 */

function parse(value) {
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at;
}

function formatLocal(value) {
  const at = parse(value);
  if (!at) return "";
  return at.toLocaleString(undefined, { timeZone: accountZone(),
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function relative(value, now) {
  const at = parse(value);
  if (!at) return "";
  const seconds = Math.round((at.getTime() - now.getTime()) / 1000);
  if (seconds > -60) return "just now";
  if (seconds > -3600) return `${Math.round(-seconds / 60)} min ago`;
  const hours = Math.floor(-seconds / 3600);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function FeedContentInspection({ content, now = new Date() }) {
  if (!content) return null;

  const retention = content.retention ?? {};
  const entries = content.entries ?? [];
  const removed = content.removed ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="ical-card">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
          <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-base)" }}>
            Cached calendar
          </p>
          {content.available ? (
            <span className="ical-badge ical-badge-neutral">
              {entries.length === 1 ? "1 event" : `${entries.length} events`}
            </span>
          ) : (
            <span className="ical-badge ical-badge-warning">nothing cached</span>
          )}
        </div>

        <p style={{ margin: "6px 0 0", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
          {content.available && content.capturedAt ? (
            <>
              Snapshot taken {relative(content.capturedAt, now)} ({formatLocal(content.capturedAt)}), as
              the worker saw it while building the changes for{" "}
              <strong>{content.externalId}</strong>. Kept for up to {retention.contentHours} hours and
              replaced by the next poll that finds a change.
            </>
          ) : (
            <>
              No snapshot is held for <strong>{content.externalId}</strong>.
            </>
          )}
        </p>

        {!content.available && content.reason ? (
          <p style={{ margin: "8px 0 0", fontSize: "var(--text-sm)" }}>{content.reason}.</p>
        ) : null}

        {content.available && entries.length === 0 ? (
          <p style={{ margin: "8px 0 0", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
            The cached calendar currently contains no events.
          </p>
        ) : null}

        {entries.length > 0 ? (
          <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "flex", flexDirection: "column", gap: 8 }}>
            {entries.map((entry, index) => (
              <li key={`${entry.uid || "entry"}-${index}`} style={{ listStyle: "none" }}>
                <EventRow event={entry} />
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="ical-card">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
          <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-base)" }}>Removed events</p>
          <span className={`ical-badge ${removed.length ? "ical-badge-warning" : "ical-badge-neutral"}`}>
            {removed.length === 1 ? "1 removed" : `${removed.length} removed`}
          </span>
        </div>

        <p style={{ margin: "6px 0 12px", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
          Events that were in an earlier snapshot and are no longer in the calendar, newest first. The
          snapshot above is overwritten by every poll, so this is the only remaining record of an event
          once it disappears — kept for up to {retention.removalsHours} hours
          {retention.removalsCapacity ? ` or ${retention.removalsCapacity.toLocaleString()} entries` : ""},
          whichever comes first.
        </p>

        {removed.length === 0 ? (
          <p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
            Nothing has disappeared from this calendar since the log began.
          </p>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {removed.map((removal, index) => (
              <li
                key={`${removal.event?.uid || "removed"}-${index}`}
                style={{ borderTop: index ? "1px solid var(--border-subtle, rgba(0,0,0,0.08))" : "none", paddingTop: index ? 8 : 0 }}
              >
                <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
                  Removed {relative(removal.detectedAt, now)} ({formatLocal(removal.detectedAt)})
                </p>
                <EventRow event={removal.event} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
        {content.notice
          ? `${content.notice} This view is intended for development and support.`
          : "This view is intended for development and support."}
      </p>
    </div>
  );
}

/** One event, already rendered by the server into readable fields. */
function EventRow({ event }) {
  if (!event) return null;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: "var(--text-sm)", fontWeight: 500 }}>{event.title}</span>
        {event.status && event.status !== "Confirmed" ? (
          <span className={`ical-badge ${/cancel/i.test(event.status) ? "ical-badge-danger" : "ical-badge-warning"}`}>
            {event.status}
          </span>
        ) : null}
        {event.allDay ? <span className="ical-badge ical-badge-neutral">all day</span> : null}
      </div>
      <p style={{ margin: "2px 0 0", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
        {event.when || "no date in the calendar"}
        {event.where ? ` · ${event.where}` : ""}
      </p>
      {event.uid ? (
        <p style={{ margin: "2px 0 0", fontSize: "var(--text-xs)", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
          {event.uid}
        </p>
      ) : null}
      {event.details ? (
        <p style={{ margin: "4px 0 0", fontSize: "var(--text-sm)", whiteSpace: "pre-wrap" }}>
          {event.details}
        </p>
      ) : null}
    </div>
  );
}
